import { useState } from 'react'
import { AlertCircle, CheckCircle, ExternalLink, Copy } from 'lucide-react'

interface GoogleDriveHelperProps {
  url: string
  onUrlChange?: (url: string) => void
  className?: string
}

export default function GoogleDriveHelper({ url, onUrlChange, className = '' }: GoogleDriveHelperProps) {
  const [convertedUrl, setConvertedUrl] = useState('')
  const [copied, setCopied] = useState(false)

  // Extract file ID from Google Drive URL
  const extractFileId = (url: string): string | null => {
    // Handle different Google Drive URL formats
    const patterns = [
      /\/file\/d\/([a-zA-Z0-9-_]+)/,  // /file/d/FILE_ID
      /id=([a-zA-Z0-9-_]+)/,         // ?id=FILE_ID
      /\/d\/([a-zA-Z0-9-_]+)/        // /d/FILE_ID
    ]
    
    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match) return match[1]
    }
    return null
  }

  // Convert to embeddable URL
  const convertToEmbedUrl = (fileId: string): string => {
    return `https://drive.google.com/file/d/${fileId}/preview`
  }

  // Handle URL conversion
  const handleConvert = () => {
    const fileId = extractFileId(url)
    if (fileId) {
      const embedUrl = convertToEmbedUrl(fileId)
      setConvertedUrl(embedUrl)
      if (onUrlChange) {
        onUrlChange(embedUrl)
      }
    }
  }

  // Copy to clipboard
  const handleCopy = async () => {
    if (convertedUrl) {
      try {
        await navigator.clipboard.writeText(convertedUrl)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy:', err)
      }
    }
  }

  const fileId = extractFileId(url)
  const isValidUrl = !!fileId

  return (
    <div className={`bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
            Google Drive Video Setup
          </h4>
          
          {isValidUrl ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                <CheckCircle className="h-4 w-4" />
                <span>Valid Google Drive URL detected</span>
              </div>
              
              <div>
                <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                  <strong>File ID:</strong> <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{fileId}</code>
                </p>
                
                {!convertedUrl && (
                  <button
                    onClick={handleConvert}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors"
                  >
                    Convert to Embed URL
                  </button>
                )}
                
                {convertedUrl && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
                      <CheckCircle className="h-4 w-4" />
                      <span>Converted to embed URL</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={convertedUrl}
                        readOnly
                        className="flex-1 px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded text-xs"
                      />
                      <button
                        onClick={handleCopy}
                        className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs transition-colors flex items-center gap-1"
                      >
                        <Copy className="h-3 w-3" />
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <p className="mb-2">To use Google Drive videos:</p>
                <ol className="list-decimal list-inside space-y-1 text-xs">
                  <li>Make sure your Google Drive file is <strong>publicly viewable</strong></li>
                  <li>Copy the sharing URL from Google Drive</li>
                  <li>Paste it in the video URL field above</li>
                  <li>The URL should look like: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">https://drive.google.com/file/d/FILE_ID/view</code></li>
                </ol>
              </div>
              
              <div className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <a 
                  href="https://support.google.com/drive/answer/2494822" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  How to share Google Drive files
                </a>
              </div>
            </div>
          )}
          
          <div className="mt-3 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-800 dark:text-amber-300">
            <strong>Note:</strong> Google Drive videos are embedded securely. Make sure your file sharing permissions are set to "Anyone with the link can view".
          </div>
        </div>
      </div>
    </div>
  )
}
